import type { ReactNode } from 'react'
import { useEffect } from 'react'

/**
 * Bottom sheet — 9 of the 51 screens (design-spec.md §5.7).
 *
 * Scrim `black/50%` with a 12px backdrop blur over the whole frame; the sheet
 * is `#111111` on a `white/13%` border with a 20px top radius and a 40×4
 * radius-2 `white/25%` grab handle.
 *
 * Sheets are routes rather than local state (the `backgroundLocation`
 * pattern), so the Android back button and deep links both work. This
 * component only paints; the router decides when it exists.
 *
 * `trade` is the newer shape the buy/sell frames draw (`731:571`, `731:717`):
 * radius 26, the handle 22 below the top edge and the content 14 under it, and
 * a lighter 6px blur on the scrim. It is a variant rather than a new default
 * because the sheets outside the trade flow are still drawn the old way.
 */
interface BottomSheetProps {
  children: ReactNode
  onDismiss: () => void
  /** Accessible name for the dialog. */
  label: string
  /** Side inset. The trade sheets use 20; the add-card sheet uses 40. */
  pad?: 'app' | 'wide'
  variant?: 'default' | 'trade'
  /**
   * `false` while something outside the sheet is in front of it — the
   * support chat, opened from the transaction sheet — so assistive tech is
   * not told that the sheet is all there is.
   */
  modal?: boolean
}

export function BottomSheet({
  children,
  onDismiss,
  label,
  pad = 'app',
  variant = 'default',
  modal = true,
}: BottomSheetProps) {
  const trade = variant === 'trade'
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss()
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [onDismiss])

  return (
    <div className="fixed inset-0 z-(--sx-z-sheet) flex flex-col justify-end">
      <button
        type="button"
        aria-label="بستن"
        onClick={onDismiss}
        className={`absolute inset-0 bg-scrim ${trade ? 'backdrop-blur-[6px]' : 'backdrop-blur-[12px]'}`}
      />
      <div
        role="dialog"
        aria-modal={modal ? 'true' : undefined}
        aria-label={label}
        className={`sx-page relative max-h-[85dvh] overflow-y-auto border-t border-line-13 bg-bg ${
          trade ? 'rounded-t-26 pb-6' : 'rounded-t-20 pb-8'
        }`}
      >
        <div className={`flex justify-center ${trade ? 'pt-[22px] pb-[14px]' : 'pt-3 pb-1'}`}>
          <span className="h-[4px] w-[40px] rounded-2 bg-overlay-25" aria-hidden="true" />
        </div>
        <div
          className={`flex flex-col gap-3 ${pad === 'wide' ? 'px-10' : 'sx-col-app'}`}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
